import React, { useEffect, ReactNode } from 'react';
import styled from 'styled-components';
import { Next, Prev } from '../../../../assets/svg/index';
import { DATA_TYPE_ERROR_MESSAGE, device } from '../../../../constants';
import { ProgressBar } from '../../../../components';
import { useSwiper } from '../../../../hooks/useSwiper';
import { MotionYOffsetBlock } from '../../../../components/Motion';

type SwiperProps<T> = {
  data: T[];
  renderItem: (item: T, index: number, length: number) => ReactNode;
};

export const ResultSwiper = <T,>({ data, renderItem }: SwiperProps<T>) => {
  const {
    isOverflow,
    isAtStart,
    isAtEnd,
    visibleIndex,
    swiperRef,
    checkForOverflow,
    updateArrowStates,
    onTouchStart,
    onTouchMove,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
    handleNextClick,
    handlePrevClick,
    barElementClick,
  } = useSwiper(data.length);

  useEffect(() => {
    checkForOverflow();
    window.addEventListener('resize', checkForOverflow);
    return () => window.removeEventListener('resize', checkForOverflow);
  }, [data, checkForOverflow]);

  useEffect(() => {
    updateArrowStates();
  }, [data, visibleIndex, updateArrowStates]);

  return (
    <Wrapper>
      {Array.isArray(data) && data.length > 0 ? (
        <SwiperContainer>
          <ElementsContainer
            $isOverflow={isOverflow}
            ref={swiperRef}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onMouseUp}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
          >
            {isOverflow && (
              <PrevArrowContainer>
                <ArrowPrev onClick={handlePrevClick} disabled={isAtStart}>
                  <Prev />
                </ArrowPrev>
              </PrevArrowContainer>
            )}

            <ElementsList>
              {data.map((item, idx) => (
                <SlideWrapper key={idx}>
                  {renderItem(item, idx, data.length)}
                </SlideWrapper>
              ))}
            </ElementsList>

            {isOverflow && (
              <NextArrowContainer>
                <ArrowNext onClick={handleNextClick} disabled={isAtEnd}>
                  <Next />
                </ArrowNext>
              </NextArrowContainer>
            )}
          </ElementsContainer>
          {isOverflow && (
            <ProgressBar
              data={data}
              visibleIndex={visibleIndex}
              barElementClick={barElementClick}
            />
          )}
        </SwiperContainer>
      ) : (
        <p>${DATA_TYPE_ERROR_MESSAGE}</p>
      )}
    </Wrapper>
  );
};

const Wrapper = styled(MotionYOffsetBlock)`
  position: relative;
  max-width: 1406px;
  margin: auto;
  display: flex;
  justify-content: center;
`;

const SwiperContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 80px;
  width: 80vw;
  max-width: 1200px;
  @media screen and (${device.sm}) {
    gap: 10px;
    width: 75vw;
  }
`;

const ElementsContainer = styled.div<{ $isOverflow: boolean }>`
  width: 100%;
  min-height: 581px;
  flex-shrink: 0;
  overflow: hidden;
  scroll-behavior: smooth;
  display: ${props => (!props.$isOverflow ? 'flex' : 'block')};
  justify-content: center;
  @media screen and (${device.sm}) {
    min-height: 0;
  }
`;

const SlideWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 5px;
  flex-shrink: 0;
`;

const ElementsList = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  transition: 2s;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-select: none;
  user-select: none;
`;

const ArrowPrev = styled.div<{ disabled: boolean }>`
  width: 100%;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};
`;

const ArrowNext = styled.div<{ disabled: boolean }>`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;

  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};
`;

const PrevArrowContainer = styled.div`
  width: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
`;

const NextArrowContainer = styled.div`
  width: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
`;
